import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Form, 
  Input, 
  Button, 
  Card, 
  Typography, 
  message, 
  Row, 
  Col, 
  Space,
  Grid
} from 'antd';
import { 
  SaveOutlined, 
  ReloadOutlined, 
  SettingOutlined,
  InfoCircleOutlined,
  PhoneOutlined,
  UserOutlined
} from '@ant-design/icons';
import { useSystem } from '../contexts/SystemContext';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { useBreakpoint } = Grid;

interface SystemSettings {
  id?: number;
  system_name: string;
  company_name: string;
  company_address: string;
  company_short_name: string;
  company_email?: string;
  company_mobile?: string;
  company_phone?: string;
  company_fax?: string;
  company_remark?: string;
}

const BasicSettings: React.FC = () => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const { systemSettings, loading, refreshSettings } = useSystem();
  const [messageApi, contextHolder] = message.useMessage();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  
  // 使用 ref 来防止重复提交
  const isSubmitting = useRef(false);

  // 保存系统设置 - 使用 useCallback 包装
  const onFinish = useCallback(async (values: SystemSettings) => {
    if (isSubmitting.current) {
      console.log('onFinish: 正在保存中，跳过重复提交');
      return;
    }

    isSubmitting.current = true;
    setSaving(true);
    try {
      // 显示保存中的消息
      messageApi.loading({
        content: '正在保存系统设置...',
        duration: 0, // 不自动关闭
        key: 'saving'
      });

      console.log('onFinish: 开始保存系统设置', values);
      const response = await fetch('/api/settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(values),
      });
      
      const result = await response.json();
      
      // 关闭保存中的消息
      messageApi.destroy('saving');
      
      if (result.success) {
        messageApi.success({
          content: '系统设置保存成功！',
          duration: 3,
          key: 'saveSuccess'
        });
        
        // 更新localStorage中的系统名称
        localStorage.setItem('systemName', values.system_name);
        // 刷新系统设置，更新全局状态
        await refreshSettings();
        console.log('onFinish: 系统设置保存成功');
      } else {
        messageApi.error({
          content: `保存失败：${result.message || '未知错误'}`,
          duration: 5,
          key: 'saveError'
        });
        console.log('onFinish: 系统设置保存失败', result.message);
      }
    } catch (error) {
      console.error('保存系统设置失败:', error);
      // 关闭保存中的消息
      messageApi.destroy('saving');
      messageApi.error({
        content: '保存失败：网络连接错误，请检查网络后重试',
        duration: 5,
        key: 'saveError'
      });
    } finally {
      setSaving(false);
      isSubmitting.current = false;
    }
  }, [messageApi, refreshSettings]);

  const handleReset = useCallback(() => {
    form.resetFields();
    messageApi.info({
      content: '设置已重置为默认值',
      duration: 2,
      key: 'resetInfo'
    });
  }, [form, messageApi]);

  // 表单验证通过后的提示
  const onFinishFailed = useCallback((errorInfo: any) => {
    messageApi.error({
      content: '表单验证失败，请检查必填字段',
      duration: 3,
      key: 'validationError'
    });
  }, [messageApi]);

  // 当 systemSettings 数据加载完成后，设置表单初始值
  useEffect(() => {
    if (systemSettings && !loading) {
      console.log('BasicSettings: 系统设置数据已加载，设置表单值', systemSettings);
      form.setFieldsValue(systemSettings);
      // 使用 key 确保只显示一次提醒
      messageApi.success({
        content: '系统设置加载成功',
        key: 'settingsLoaded',
        duration: 2
      });
    }
  }, [systemSettings, loading, form, messageApi]);

  // 如果还在加载中，显示加载状态
  if (loading) {
    return (
      <div style={{ padding: isMobile ? '16px' : '24px', textAlign: 'center' }}>
        <div style={{ marginTop: '50px' }}>
          <div>正在加载系统设置...</div>
        </div>
      </div>
    );
  }

  return (
    <>
      {contextHolder}
      <div style={{ padding: isMobile ? '16px' : '24px' }}>
        <Card>
          <div style={{ marginBottom: isMobile ? '16px' : '24px' }}>
            <Row gutter={[12, 12]} align="middle">
              <Col xs={24} sm={24} md={24}>
                <Title level={2} style={{ margin: 0, fontSize: isMobile ? '20px' : '24px' }}>
                  <SettingOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                  基本设置
                </Title>
                <Text type="secondary">管理系统基本信息和配置</Text>
              </Col>
            </Row>
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            initialValues={{
              system_name: '智能物流管理系统',
              company_name: '智慧物流科技有限公司',
              company_address: '河北三河',
              company_short_name: '智慧物流',
              company_email: 'admin@company.com',
              company_mobile: '400-123-4567',
              company_phone: '010-12345678',
              company_fax: '010-12345679',
              company_remark: '',
            }}
          >
            {/* 系统信息区域 */}
            <Card 
              title={
                <Space>
                  <InfoCircleOutlined style={{ color: '#1890ff' }} />
                  <span>系统信息</span>
                </Space>
              } 
              size="small" 
              style={{ marginBottom: 16 }}
              headStyle={{ backgroundColor: '#fafafa' }}
            >
              {isMobile ? (
                // 移动端单列布局
                <>
                  <Form.Item 
                    name="system_name" 
                    label="系统名称" 
                    rules={[{ required: true, message: '请输入系统名称' }]}
                  >
                    <Input placeholder="请输入系统名称" />
                  </Form.Item>

                  <Form.Item 
                    name="company_name" 
                    label="公司名称" 
                    rules={[{ required: true, message: '请输入公司名称' }]}
                  >
                    <Input placeholder="请输入公司名称" />
                  </Form.Item>

                  <Form.Item 
                    name="company_short_name" 
                    label="公司简称" 
                    rules={[{ required: true, message: '请输入公司简称' }]}
                  >
                    <Input placeholder="请输入公司简称" />
                  </Form.Item>

                  <Form.Item 
                    name="company_address" 
                    label="公司地址" 
                    rules={[{ required: true, message: '请输入公司地址' }]}
                  >
                    <Input placeholder="请输入公司地址" />
                  </Form.Item>
                </>
              ) : (
                // PC端两列布局
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item 
                      name="system_name" 
                      label="系统名称" 
                      rules={[{ required: true, message: '请输入系统名称' }]}
                    >
                      <Input placeholder="请输入系统名称" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item 
                      name="company_name" 
                      label="公司名称" 
                      rules={[{ required: true, message: '请输入公司名称' }]}
                    >
                      <Input placeholder="请输入公司名称" />
                    </Form.Item>
                  </Col>
                </Row>
              )}

              {!isMobile && (
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item 
                      name="company_short_name" 
                      label="公司简称" 
                      rules={[{ required: true, message: '请输入公司简称' }]}
                    >
                      <Input placeholder="请输入公司简称" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item 
                      name="company_address" 
                      label="公司地址" 
                      rules={[{ required: true, message: '请输入公司地址' }]}
                    >
                      <Input placeholder="请输入公司地址" />
                    </Form.Item>
                  </Col>
                </Row>
              )}
            </Card>

            {/* 联系方式区域 */}
            <Card 
              title={
                <Space>
                  <PhoneOutlined style={{ color: '#52c41a' }} />
                  <span>联系方式</span>
                </Space>
              } 
              size="small" 
              style={{ marginBottom: 16 }}
              headStyle={{ backgroundColor: '#fafafa' }}
            >
              {isMobile ? (
                // 移动端单列布局
                <>
                  <Form.Item 
                    name="company_email" 
                    label="公司邮箱"
                  >
                    <Input placeholder="请输入公司邮箱" />
                  </Form.Item>

                  <Form.Item 
                    name="company_mobile" 
                    label="公司手机"
                  >
                    <Input placeholder="请输入公司手机" />
                  </Form.Item>

                  <Form.Item 
                    name="company_phone" 
                    label="公司电话"
                  >
                    <Input placeholder="请输入公司电话" />
                  </Form.Item>

                  <Form.Item 
                    name="company_fax" 
                    label="公司传真"
                  >
                    <Input placeholder="请输入公司传真" />
                  </Form.Item>
                </>
              ) : (
                // PC端两列布局
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item 
                      name="company_email" 
                      label="公司邮箱"
                    >
                      <Input placeholder="请输入公司邮箱" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item 
                      name="company_mobile" 
                      label="公司手机"
                    >
                      <Input placeholder="请输入公司手机" />
                    </Form.Item>
                  </Col>
                </Row>
              )}

              {!isMobile && (
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item 
                      name="company_phone" 
                      label="公司电话"
                    >
                      <Input placeholder="请输入公司电话" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item 
                      name="company_fax" 
                      label="公司传真"
                    >
                      <Input placeholder="请输入公司传真" />
                    </Form.Item>
                  </Col>
                </Row>
              )}
            </Card>

            {/* 备注信息区域 */}
            <Card 
              title={
                <Space>
                  <UserOutlined style={{ color: '#722ed1' }} />
                  <span>备注信息</span>
                </Space>
              } 
              size="small" 
              style={{ marginBottom: 24 }}
              headStyle={{ backgroundColor: '#fafafa' }}
            >
              <Form.Item
                label="备注"
                name="company_remark"
              >
                <TextArea 
                  rows={4} 
                  placeholder="请输入备注信息"
                  showCount
                  maxLength={500}
                />
              </Form.Item>
            </Card>

            {/* 操作按钮区域 */}
            <div style={{ textAlign: 'right', marginTop: 24 }}>
              <Space size="middle">
                <Button 
                  icon={<ReloadOutlined />} 
                  onClick={handleReset} 
                  size={isMobile ? 'middle' : 'large'}
                >
                  重置设置
                </Button>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={saving}
                  icon={<SaveOutlined />}
                  size={isMobile ? 'middle' : 'large'}
                >
                  {saving ? '保存中...' : '保存设置'}
                </Button>
              </Space>
            </div>
          </Form>
        </Card>
      </div>
    </>
  );
};

export default BasicSettings;
